import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useScreenSize, useCrispChat } from '@/hooks';
import { gsap } from 'gsap';
import MenuIcon from '@/components/icons/MenuIcon';
import ChatIcon from '@/components/icons/ChatIcon';
import GlobeIcon from '@/components/icons/GlobeIcon';
import SearchIcon from '@/components/icons/SearchIcon';
import LoginButton from '@/components/auth/LoginButton';
import RegisterButton from '@/components/auth/RegisterButton';
import Wallet from '@/components/wallet/Wallet';
import ProfileMenu from '@/components/common/ProfileMenu/ProfileMenu';
import styles from './Header.module.css';

/**
 * Header Component
 * Main header with logo, navigation, and user actions
 * Matches old Header-Guest structure for non-logged in users
 */
const Header = ({ onMenuToggle, isSidebarCollapsed }) => {
  const { isMobile } = useScreenSize();
  const location = useLocation();
  const navigate = useNavigate();
  const { handleSupportClick } = useCrispChat();
  const [showFullLogo, setShowFullLogo] = useState(!isMobile);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const bonusIconRef = useRef(null);

  useEffect(() => {
    setShowFullLogo(!isMobile);

    // Check login status
    const checkAuthStatus = () => {
      const token = localStorage.getItem('token');
      setIsLoggedIn(!!token);
    };

    checkAuthStatus();
    window.addEventListener('storage', checkAuthStatus);
    return () => window.removeEventListener('storage', checkAuthStatus);
  }, [isMobile]);

  // GSAP animation for bonus icon - simple blink only
  useEffect(() => {
    if (bonusIconRef.current) {
      const element = bonusIconRef.current;
      
      // Continuous slow blinking animation
      const blinkTimeline = gsap.timeline({ repeat: -1 });
      blinkTimeline.to(element, {
        opacity: 0.4,
        duration: 2,
        ease: 'power1.inOut',
      });
      blinkTimeline.to(element, {
        opacity: 1,
        duration: 2,
        ease: 'power1.inOut',
      });

      return () => {
        blinkTimeline.kill();
      };
    }
  }, []);

  // Check if current route is sports-related
  const isSportsRoute = location.pathname.startsWith('/sports/') ||
    location.pathname.startsWith('/match/');

  return (
    <header className={styles.header}>
      <div className={styles.container}>
        {/* Left Section */}
        <div className={styles.leftSection}>
          {!isMobile && (
            <button
              className={styles.menuButton}
              onClick={onMenuToggle}
              aria-label="Toggle sidebar"
            >
              <MenuIcon size="24px" />
            </button>
          )}

          <Link to="/" className={styles.logoLink}>
            <img
              src={isLoggedIn && isMobile
                ? '/assets/images/logo/mobile-logo.webp'
                : '/assets/images/logo/web-logo.webp'}
              alt="JACKOPOT"
              className={styles.logo}
              style={isLoggedIn && isMobile
                ? { width: 'auto', maxWidth: '120px', maxHeight: '40px' }
                : { width: showFullLogo ? '128px' : '96px' }}
              onError={(e) => {
                e.target.src = '/assets/images/lg.png';
              }}
            />
          </Link>
        </div>

        {/* Center Section - Wallet (for logged in users, mobile only) */}
        {isLoggedIn && isMobile && (
          <div className={styles.centerSection}>
            <Wallet />
          </div>
        )}

        {/* Right Section */}
        <div className={styles.rightSection}>
          {/* Bonus Icon (mobile and web) */}
         {
          isLoggedIn && (
            <div
            // ref={bonusIconRef}
            onClick={() => navigate('/gift-cards')}
            aria-label="Bonus"
            className={styles.bonusIconButton}
            style={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img
              src="/assets/images/bonus/2.png"
              alt="Bonus"
              className={styles.bonusIconImage}
              loading="lazy"
            />
          </div>
          )
         }

          {/* Search Icon (for logged in users, desktop only) */}
          {isLoggedIn && !isMobile && (
            <button
              className={styles.iconButton}
              onClick={() => navigate('/modal?mode=search')}
              aria-label="Search games"
            >
              <SearchIcon size="20px" />
            </button>
          )}

          {/* Wallet (for logged in users, desktop only) */}
          {isLoggedIn && !isMobile && <Wallet />}

          {/* Auth Buttons (for non-logged in users) */}
          {!isLoggedIn && (
            <div className={styles.authButtons}>
              <LoginButton />
              <RegisterButton />
            </div>
          )}

          {/* Action Icons (for non-logged in users) */}
          {!isLoggedIn && !isMobile && (
            <div className={styles.iconGroup}>
              <button 
                className={styles.iconButton} 
                aria-label="Messages"
                onClick={handleSupportClick}
              >
                <ChatIcon size="24px" />
              </button>
              <button className={styles.iconButton} aria-label="Language">
                <GlobeIcon size="24px" />
              </button>
            </div>
          )}

          {/* Profile Menu (for logged in users - both mobile and desktop) */}
          {isLoggedIn && <ProfileMenu />}
        </div>
      </div>
    </header>
  );
};

export default Header;
